<template>
  <button class="IconButton" type="button" :class="[mode, { hover, focus, showBadge }]" :disabled="disabled">
    <div class="Icon">
      <slot></slot>
    </div>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  props: {
    mode: {
      type: String as () => 'primary' | 'secondary' | 'tertiary' | 'distructive',
      default: 'primary'
    },
    hover: {
      type: Boolean,
      default: false
    },
    focus: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showBadge: {
      type: Boolean,
      default: false
    }
  },
})
</script>

<style scoped>
.IconButton {
  appearance: none;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: .3s;
  padding: 6px;
  width: 36px;
  height: 36px;
  box-sizing: border-box;
  position: relative;
}

.IconButton.showBadge::before {
  content: '';
  position: absolute;
  right: -4px;
  top: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: solid 1px #fff;
  background: linear-gradient(89deg, #FE1D60 0%, #F37C0D 100%);
}

.IconButton:disabled {
  cursor: not-allowed;
}

.IconButton.primary {
  background: var(--primitive-colors-basic-yellow-400);
}

.IconButton.secondary {
  background: var(--primitive-colors-neutral-white-500);
  border: 1px solid var(--semantic-colors-border-box);
}

.IconButton.tertiary {
  background: transparent;
  border-radius: 0;
}

.IconButton.distructive {
  background: var(--primitive-colors-system-red-500);
}

.IconButton.primary:not([disabled]):hover,
.IconButton.primary.hover {
  background: var(--primitive-colors-basic-yellow-500);
}

.IconButton.secondary:not([disabled]):hover,
.IconButton.secondary.hover {
  background: var(--primitive-colors-neutral-white-600);
}

.IconButton.tertiary:not([disabled]):hover,
.IconButton.tertiary.hover {
  background: var(--primitive-colors-neutral-white-300);
}

.IconButton.distructive:not([disabled]):hover,
.IconButton.distructive.hover {
  background: var(--primitive-colors-system-red-600);
}

.IconButton.primary:focus,
.IconButton.primary.focus {
  background: var(--primitive-colors-basic-yellow-400);
  outline: solid 3px var(--primitive-colors-basic-yellow-200);
}

.IconButton.secondary:focus,
.IconButton.secondary.focus {
  background: var(--primitive-colors-neutral-white-500);
  outline: solid 3px var(--primitive-colors-neutral-white-600);
}

.IconButton.tertiary:focus,
.IconButton.tertiary.focus {
  outline: solid 3px var(--semantic-colors-border-section);
}

.IconButton.distructive:focus,
.IconButton.distructive.focus {
  outline: solid 3px var(--primitive-colors-system-red-200);
}

.IconButton.primary:disabled {
  background: var(--primitive-colors-basic-yellow-200);
}

.IconButton.distructive:disabled {
  background: var(--primitive-colors-system-red-200);
}

.IconButton .Icon > *, :slotted(.IconButton .Icon > *) {
  width: 100%;
  height: 100%;
}

.IconButton .Icon > *, :slotted(.IconButton .Icon > *) {
  fill: var(--semantic-colors-text-black);
}

.IconButton.distructive .Icon > *, :slotted(.IconButton.distructive .Icon > *) {
  fill: var(--semantic-colors-text-white);
}

.IconButton:disabled .Icon > *, :slotted(.IconButton:disabled .Icon > *) {
  fill: var(--semantic-colors-text-disabled);
}

.IconButton.distructive:disabled .Icon > *, :slotted(.IconButton.distructive:disabled .Icon > *) {
  fill: var(--semantic-colors-text-white);
}
</style>